import { useEffect, useState } from 'react';
import type { EventPageFragment } from '@graphql/fragments/event.page.graphql.generated';
import { useScrollToPageWhenUrlChanges } from '@apps/guest/packages/layout-engine/layouts/LayoutAloha/hooks/useScrollToPageWhenUrlChanges';
import { useKeepPageScrollPosition } from '@apps/guest/packages/layout-engine/layouts/LayoutAloha/hooks/useKeepPageScrollPosition';
import type { PagesRefs } from '@apps/guest/packages/layout-engine/layouts/LayoutAloha/LayoutAloha.types';
import { usePageInScreenWhenScrolling } from '@apps/guest/packages/layout-engine/layouts/LayoutAloha/hooks/usePageInScreenWhenScrolling';
import { usePageScrollAtTheBottom } from '@apps/guest/packages/layout-engine/layouts/LayoutAloha/hooks/usePageScrollAtTheBottom';

/**
 * UseAlohaScroll is the pages' scroll controller. It's composed of 4 features:
 * 1. Scroll to the right event page when the page loads or when the user navigates to the event page through the menu or URL.
 *    This behavior will be addressed by the useScrollToPageWhenUrlChanges hook
 *
 * 2. Event pages load lazily and asynchronously, Then if the user is watching an event page, an above event page can load content,
 *    and it will move down the scroll (Event page jumps). The current fix is listening for the DOM changes on Event Pages
 *    then moving the scroll the difference between the old height and the new height. It will move the scroll to keep the Event Page in the position
 *    that the user is watching avoiding the jump for the user.
 *    This behavior is handled by the useKeepPageScrollPosition hook
 *
 * 3. When the user starts scrolling, we update the URL to reflect the page on the visible screen. We will update the URL once
 *    the page is 80% visible.
 *    This behavior is managed by the usePageInScreenWhenScrolling hook
 *
 * 4. When the scroll thumb reached the scroll bar bottom, it will update the setIsLastPageVisible state.
 *    Some components like Action Bar need to know the scroll thumb is at the end to show up and trigger another action.
 *    When the user has scrolled past the first page (first page fully above viewport), setIsPastFirstPage(true) is called.
 *    This behavior is covered by usePageScrollAtTheBottom hook
 */
export const useAlohaScroll = (
  pages: ReadonlyArray<EventPageFragment>,
  pageRefList: Record<string, PagesRefs>,
  container: HTMLDivElement | null,
  setIsPageScrollAtTheBottom?: (value: boolean) => void,
  setIsPastFirstPage?: (value: boolean) => void,
) => {
  const [pagesRef, setPagesRef] = useState<PagesRefs[]>([]);
  const { currentPage, hasScrolled, isCurrentPageFirst } = useScrollToPageWhenUrlChanges({ pagesRef, container });

  useEffect(() => {
    if (!pageRefList) return;
    const currentPagesArray = Object.keys(pageRefList).map(page => pageRefList[page]);
    // Keep the previous array when nothing actually changed. This hook's state sits in
    // LayoutAloha's hook list, so an unconditional set re-rendered every section of the site —
    // the single largest source of full-page re-renders on Aloha.
    setPagesRef(previous => (previous.length === currentPagesArray.length && previous.every((entry, index) => entry === currentPagesArray[index]) ? previous : currentPagesArray));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pages]);

  // Reuses the single computation from useScrollToPageWhenUrlChanges rather than re-deriving it, so
  // the landing and the pinning carve-out cannot drift apart. Compared by page id there, never by
  // ref: a section remount swaps the DOM node but keeps the id.
  useKeepPageScrollPosition({ pagesRef, container, targetPage: currentPage?.ref, isTargetFirstPage: isCurrentPageFirst });
  usePageInScreenWhenScrolling({ pagesRef, container, currentPageInTheUrl: currentPage?.page, hasScrolled });
  usePageScrollAtTheBottom({
    container,
    setIsPageScrollAtTheBottom,
    pagesRef,
    setIsPastFirstPage,
  });
};
